import { isSessionBlocked } from '@/core';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { ApiError, clearSession as clearTokens, getSession } from '@/core';
import { ensureCsrfToken, loadCurrentUser, logout } from '@/api/auth';
import { csrfToken } from '@/api/httpClient';
import type { ClinicCompany, ClinicUser } from '@/domain/session';

/**
 * C4 — sessão da clínica.
 *
 * O access token vive em memória, dentro do core vendorizado (`@/core`), e nunca em
 * `localStorage`/`sessionStorage`; o refresh fica em cookie HttpOnly do BFF. Esta store guarda
 * apenas a identidade de negócio (C3), que o `SessionUser` de C2 não modela (ele fixa
 * `persona: 'operacional'`).
 */
const user = ref<ClinicUser | null>(null);

export function useSessionStore(): {
  user: Ref<ClinicUser | null>;
  activeCompany: ComputedRef<ClinicCompany | null>;
  signOut: () => Promise<void>;
} {
  const activeCompany = computed<ClinicCompany | null>(() => {
    const current = user.value;
    if (current === null) return null;
    return current.companies.find((company) => company.id === current.activeCompanyId) ?? current.companies[0] ?? null;
  });

  async function signOut(): Promise<void> {
    user.value = null;
    await logout();
  }

  return { user, activeCompany, signOut };
}

export function setCurrentUser(next: ClinicUser): void {
  user.value = next;
}

/**
 * Troca a unidade ativa da sessão (multi-clínica). Estado em memória: ao recarregar, o
 * servidor volta a decidir pela `companyId` do `/auth/me` — nenhuma preferência é persistida
 * no cliente. Quem troca de clínica também recarrega as stores por unidade (portal etc.).
 */
export function setActiveCompany(companyId: string): void {
  const current = user.value;
  if (current === null) return;
  if (!current.companies.some((company) => company.id === companyId)) return;
  user.value = { ...current, activeCompanyId: companyId };
}

export type SessionGuardResult =
  | { readonly status: 'authenticated' }
  | { readonly status: 'unauthenticated' }
  /** Falha transiente COM sessão em memória: as views já têm dados, a navegação segue. */
  | { readonly status: 'unavailable'; readonly error: ApiError }
  /** Falha transiente SEM nada em memória (reload frio): não há shell a mostrar, só a tela de indisponibilidade. */
  | { readonly status: 'unreachable'; readonly error: ApiError };

/**
 * Falha transiente (rede, timeout, CORS, 5xx) não é sessão inválida: o BFF pode estar fora do ar
 * por um instante. Deslogar nesse caso expulsaria quem estava logado.
 */
function isTransient(error: unknown): error is ApiError {
  if (!(error instanceof ApiError)) return false;
  return error.kind !== 'http' || error.status >= 500;
}

/**
 * Guard de rota: já há sessão utilizável? Um `GET /auth/me` bem-sucedido prova cookie + token
 * válidos de uma vez — o refresh silencioso já está embutido no `httpClient` de C4.
 *
 * Num reload o estado em memória é zerado, mas o cookie de sessão do BFF continua válido: o
 * `/auth/me` responde 200 e a navegação seguiria SEM token CSRF, fazendo toda mutação tomar 403.
 * Por isso o CSRF é rehidratado (`GET /auth/csrf`) antes de liberar a navegação — e antes do
 * `/auth/me`, porque o refresh silencioso do httpClient também depende do header.
 *
 * Quatro desfechos, nunca um booleano solto: sessão válida, sessão inválida (401/contrato quebrado
 * → limpa o estado e manda ao login) e indisponibilidade transiente (rede/5xx → preserva a
 * sessão e devolve o erro para a apresentação tratar com estado de erro e retry, em `unavailable`
 * quando havia sessão em memória ou em `unreachable` no reload frio).
 */
export async function ensureSession(): Promise<SessionGuardResult> {
  if (isSessionBlocked()) return { status: 'unauthenticated' };
  const hasIdentity = getSession().accessToken !== null && user.value !== null;
  // Mesma condição de `ensureCsrfToken` (inclui o fallback de cookie same-site), para o guard e a
  // busca não divergirem.
  if (hasIdentity && csrfToken() !== null) return { status: 'authenticated' };
  try {
    await ensureCsrfToken();
    if (!hasIdentity) user.value = await loadCurrentUser();
    if (isSessionBlocked()) return { status: 'unauthenticated' };
    return { status: 'authenticated' };
  } catch (error) {
    // Com identidade em memória o usuário segue navegando (blip do BFF); sem identidade não há
    // nada para mostrar — o guard leva à tela de indisponibilidade, nunca ao login nem ao shell vazio.
    if (isTransient(error)) return hasIdentity ? { status: 'unavailable', error } : { status: 'unreachable', error };
    user.value = null;
    clearTokens();
    return { status: 'unauthenticated' };
  }
}
